import { Badge, useTheme } from '@mui/material';
import { BaseTheme } from 'design-system';
import { useMemo } from 'react';
import { IconType } from 'react-icons';
import useButtonColor from 'hooks/useButtonColor';
import { ButtonColor } from 'generated/graphql';

const iconStyle = (theme: BaseTheme, marginRight = 0, marginLeft = 0) => ({
  fontSize: '1.6em',
  margin: theme.spacing(-0.25, marginRight, 0, marginLeft),
});

const badgeTextStyle = (theme: BaseTheme) => ({
  height: theme.spacing(2),
  minWidth: theme.spacing(2.5),
  padding: theme.spacing(0, 0.625),
  borderRadius: theme.spacing(1),
  lineHeight: 1,
  fontSize: theme.typography.pxToRem(11),
  fontWeight: theme.typography.fontWeightBold,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const defaultSpanStyle = (theme: BaseTheme) => ({
  ...badgeTextStyle(theme),
  transform: 'translate(65%, -70%)',
  background: theme.palette.info.main,
});

const providerSpanStyle = (theme: BaseTheme, color?: string | null) => {
  return {
    ...badgeTextStyle(theme),
    transform: 'translate(40%, -10%)',
    background: color ?? theme.palette.success.light,
  };
};

const defaultBadgeStyle = (theme: BaseTheme) => ({
  marginLeft: 0,
  '& > svg': iconStyle(theme, 0, 0.5),
  '& .MuiBadge-badge': defaultSpanStyle(theme),
});

const providerBadgeStyle = (theme: BaseTheme, color?: string | null) => ({
  marginLeft: theme.spacing(-0.5),
  '& > svg': iconStyle(theme, 2, 0),
  '& .MuiBadge-badge': providerSpanStyle(theme, color),
});

const HeaderBadge = ({
  Icon,
  badgeContent,
  color,
}: {
  Icon: IconType;
  badgeContent: string | number;
  color?: ButtonColor;
}) => {
  const theme = useTheme();
  const buttonColor = useButtonColor(theme, color);

  const badgeStyle = useMemo(
    () => (badgeContent !== 0 ? providerBadgeStyle(theme, buttonColor) : defaultBadgeStyle(theme)),
    [buttonColor, badgeContent, theme],
  );

  return (
    <Badge badgeContent={badgeContent} sx={badgeStyle}>
      <Icon size={16} />
    </Badge>
  );
};

export default HeaderBadge;
